import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Card, Checkbox, Flex, Popconfirm, Typography } from 'antd';
import dayjs from 'dayjs';

import Modal from '/ui/Modal';

import filesActions from '../../../actions';

import AiIcon from '../../../../../components/AiIcon';

import { message } from '../../../../../utils/message';
import {
  getTerminalIframeUrl,
  normalizeTerminalHost,
} from '../../../utils/atlas';
import {
  downloadFileByUrl,
  getFileDownloadName,
  getFileDownloadUrl,
} from '../../../utils/download';

import FileEditableName from '../FileEditableName';
import NeuroGenerationStatus from '../NeuroGenerationStatus';
import UploadFileModal from '../UploadFileModal';

import { AppDispatch, ReduxState } from '../../../../../types/app';
import { AiFileItem, AiFileStatus } from '../../../types';

const { Text } = Typography;

type Props = {
  file: AiFileItem;
  iconName: string;
  selectable?: boolean;
  selected?: boolean;
  onSelectChange?: (checked: boolean) => void;
  onAtlasIframeClose: () => void;
};

const FileCard = ({
  file,
  iconName,
  selectable = false,
  selected = false,
  onSelectChange,
  onAtlasIframeClose,
}: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const terminalHost = useSelector(
    (state: ReduxState) => state.Data.config?.terminalHost
  );
  const themeType = useSelector(
    (state: ReduxState) => state.Data.config?.themeType
  );
  const brokerId = useSelector((state: ReduxState) => state.Data.broker?.Id);
  const clientId = useSelector((state: ReduxState) => state.Client.data?.Id);
  const [removing, setRemoving] = useState(false);
  const [changingNeuroStatus, setChangingNeuroStatus] = useState(false);
  const [replaceModalVisible, setReplaceModalVisible] = useState(false);
  const [atlasModalVisible, setAtlasModalVisible] = useState(false);
  const [atlasIframeUrl, setAtlasIframeUrl] = useState<string | null>(null);
  const isBusy = removing || changingNeuroStatus;
  const isNeuroError = file.Status === AiFileStatus.Error;
  const isNeuroPending = file.Status === AiFileStatus.Pending;
  const isNeuroProcessing = file.Status === AiFileStatus.Processing;
  const isNeuroProcessed = file.Status === AiFileStatus.Processed;
  const isNeuroDraft = file.Status === AiFileStatus.Draft;
  const showNeuroGenerationLabel = isNeuroPending || isNeuroProcessing;
  const shouldShowNeuroStatus =
    isNeuroError || showNeuroGenerationLabel || isNeuroProcessed;
  const neuroStatusTextId = isNeuroError
    ? 'files.card.errorNeuro'
    : 'files.card.processedNeuro';
  const neuroStatusClassName = isNeuroError
    ? 'card__processing card__processing_error'
    : isNeuroProcessed
      ? 'card__processing card__processing_processed'
      : 'card__processing card__processing_generation';
  const hasTerminalHost = useMemo(
    () => Boolean(normalizeTerminalHost(terminalHost)),
    [terminalHost]
  );
  const downloadUrl = useMemo(
    () => getFileDownloadUrl(brokerId, clientId, file.Key, 'csv'),
    [brokerId, clientId, file.Key]
  );
  const neuroDownloadUrl = useMemo(
    () => getFileDownloadUrl(brokerId, clientId, file.Key, 'neuro'),
    [brokerId, clientId, file.Key]
  );

  const getFileStatusIcon = () => {
    if (isNeuroError) {
      return <AiIcon name="error" />;
    }

    if (isNeuroProcessed) {
      return <AiIcon name="success" />;
    }

    return null;
  };

  const handleDownload = useCallback(() => {
    if (!downloadUrl) {
      return;
    }

    void downloadFileByUrl(
      downloadUrl,
      getFileDownloadName(file.Name, 'csv')
    ).catch((err) => {
      console.error(err);
    });
  }, [downloadUrl, file.Name]);

  const handleNeuroDownload = useCallback(() => {
    if (!neuroDownloadUrl) {
      return;
    }

    void downloadFileByUrl(
      neuroDownloadUrl,
      getFileDownloadName(file.Name, 'neuro')
    ).catch((err) => {
      console.error(err);
    });
  }, [neuroDownloadUrl, file.Name]);

  const handleOpenAtlasModal = useCallback(() => {
    const iframeUrl = getTerminalIframeUrl(
      terminalHost,
      file.Id,
      intl.locale,
      themeType,
      { appPath: true, mode: 'mode-neural-generator' }
    );

    if (!iframeUrl) return;

    setAtlasIframeUrl(iframeUrl);
    setAtlasModalVisible(true);
  }, [file.Id, intl.locale, terminalHost, themeType]);

  const handleCloseAtlasModal = useCallback(() => {
    setAtlasModalVisible(false);
    onAtlasIframeClose();
  }, [onAtlasIframeClose]);

  const handleRemove = useCallback(async () => {
    if (isBusy) {
      return;
    }

    setRemoving(true);

    try {
      await dispatch(filesActions.Files.removeFile(file.Id));
    } catch (err) {
      console.error(err);
      message.error('files.remove.error');
    } finally {
      setRemoving(false);
    }
  }, [dispatch, file.Id, isBusy]);

  const handleChangeNeuroStatus = useCallback(async () => {
    if (isBusy) {
      return;
    }

    setChangingNeuroStatus(true);

    try {
      await dispatch(
        filesActions.Files.changeFileStatus(
          file.Id,
          isNeuroPending ? 'Draft' : 'Pending'
        )
      );
    } catch (err) {
      console.error(err);
      message.error(err);
    } finally {
      setChangingNeuroStatus(false);
    }
  }, [dispatch, file.Id, isBusy, isNeuroPending]);

  return (
    <Card
      className={`files-page__card${
        selected ? ' files-page__card_selected' : ''
      }`}
    >
      <Flex className="files-page__card-header" align="flex-start" gap={12}>
        {selectable && (
          <Checkbox
            checked={selected}
            onChange={(event) => onSelectChange?.(event.target.checked)}
          />
        )}
        <span className="files-page__card-icon">
          <AiIcon name={iconName} />
        </span>

        <Flex className="files-page__card-meta" vertical flex={1} gap={2}>
          <FileEditableName
            fileId={file.Id}
            name={file.Name}
            justify="space-between"
          />
          <Flex justify="space-between" align="center">
            <Text className="files-page__card-date">
              {dayjs.unix(file.DateCreate).format('DD.MM.YYYY')}
            </Text>
          </Flex>
        </Flex>
      </Flex>

      <div className="files-page__card-actions">
        <Button
          className="btn-secondary"
          icon={<AiIcon name="download" />}
          disabled={!downloadUrl}
          onClick={handleDownload}
        >
          <FormattedMessage id="files.card.download" />
        </Button>
        <Button
          icon={<AiIcon name="changeAtlas" />}
          disabled={!hasTerminalHost}
          onClick={handleOpenAtlasModal}
        >
          <FormattedMessage id="files.card.changeAtlas" />
        </Button>
        {isNeuroDraft && (
          <Popconfirm
            title={intl.formatMessage({ id: 'popConfirm.title' })}
            okText={intl.formatMessage({ id: 'buttons.yes' })}
            cancelText={intl.formatMessage({ id: 'buttons.no' })}
            disabled={isBusy}
            onConfirm={() => void handleChangeNeuroStatus()}
          >
            <Button
              icon={<AiIcon name="ai" />}
              loading={changingNeuroStatus}
              disabled={isBusy}
            >
              <FormattedMessage id="files.card.addNeuro" />
            </Button>
          </Popconfirm>
        )}
        {shouldShowNeuroStatus && (
          <div className="processing__status-actions">
            {showNeuroGenerationLabel ? (
              <NeuroGenerationStatus
                dateUpdate={file.DateUpdate}
                className={neuroStatusClassName}
                labelClassName="card__processing-label"
                spinStyle={{ marginRight: 8 }}
              />
            ) : isNeuroProcessed ? (
              <Button
                icon={<AiIcon name="download" />}
                disabled={!neuroDownloadUrl}
                onClick={handleNeuroDownload}
                className="btn-secondary"
              >
                <FormattedMessage id="files.card.downloadNeuro" />
              </Button>
            ) : (
              <span className={neuroStatusClassName}>
                {getFileStatusIcon()}
                <span className="card__processing-label">
                  <FormattedMessage id={neuroStatusTextId} />
                </span>
              </span>
            )}
            {isNeuroPending && (
              <Popconfirm
                title={intl.formatMessage({ id: 'popConfirm.title' })}
                okText={intl.formatMessage({ id: 'buttons.yes' })}
                cancelText={intl.formatMessage({ id: 'buttons.no' })}
                disabled={isBusy}
                onConfirm={() => void handleChangeNeuroStatus()}
              >
                <Button
                  type="link"
                  className="processing__cancel-btn"
                  loading={changingNeuroStatus}
                  disabled={isBusy}
                >
                  <FormattedMessage id="files.card.cancel" />
                </Button>
              </Popconfirm>
            )}
          </div>
        )}
        <Popconfirm
          title={intl.formatMessage({ id: 'popConfirm.title' })}
          okText={intl.formatMessage({ id: 'buttons.yes' })}
          cancelText={intl.formatMessage({ id: 'buttons.no' })}
          disabled={isBusy}
          onConfirm={() => void handleRemove()}
        >
          <Button
            icon={<AiIcon name="delete" />}
            loading={removing}
            disabled={isBusy}
            danger
          >
            <FormattedMessage id="files.card.remove" />
          </Button>
        </Popconfirm>
      </div>

      <UploadFileModal
        visible={replaceModalVisible}
        fileId={file.Id}
        onClose={() => {
          setReplaceModalVisible(false);
        }}
      />
      <Modal
        className="files-page__atlas-modal"
        open={atlasModalVisible}
        onCancel={handleCloseAtlasModal}
        footer={null}
        centered
        destroyOnHidden
        width="100%"
      >
        <iframe
          className="files-page__atlas-iframe"
          src={atlasIframeUrl || undefined}
          title={intl.formatMessage({ id: 'files.card.changeAtlas' })}
        />
      </Modal>
    </Card>
  );
};

export default FileCard;
